import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeOutlined';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import CheckIcon from '@mui/icons-material/CheckCircleOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
import type { Theme } from '@mui/material/styles';
import {
  alpha,
  Box,
  BrandBlock,
  GlassChip,
  GlassIconButton,
  GlassTooltip,
  iconSizes,
  Typography,
  useI18n,
  useTerminology,
} from '@ops-matrix/design-system-ui';
import { Outlet } from 'react-router-dom';
import { useTheme } from '../theme/ThemeProvider';

/**
 * AuthLayout — v3 split-pane auth shell (ISSUE #1749 follow-up).
 *
 * Left pane (marketing): brand badge, big tagline, Astor recommendation card
 * mockup, trust line at the bottom. Static across all auth surfaces — gives
 * users the "this is what you're signing into" reassurance.
 *
 * Right pane (form, 460px on desktop): tenant logo/title above an Outlet that
 * renders the active auth page (Login / Register / MagicLink / ForgotPassword
 * / MFA / ResetPassword / AcceptInvite / Logout).
 *
 * On mobile (<= md), the marketing pane is hidden and the right pane fills the
 * viewport. The form pane is scrollable independently so long forms (Register,
 * MFA setup) never cause double scrollbars.
 */
export default function AuthLayout() {
  const { tenantTheme, portalType, mode, toggleMode } = useTheme();
  const { t } = useI18n();
  // Platform name comes from tenant terminology overrides — falls back to the
  // `navigation.platformName` i18n value (`'Fiops'` in en.ts). Tenants override
  // this via their business.terminology config.
  const { term } = useTerminology();
  const platformName = term('navigation.platformName');

  // Tenant branding (companyName / logo) supplied by ThemeProvider → resolved
  // from BusinessContext at runtime. When unset, fall back to the platform
  // brand: BrandBlock initials derived from `platformName` and no logoUrl.
  const tenantBrandName = tenantTheme?.companyName ?? platformName;
  const isWhiteLabel = !!tenantTheme?.companyName;
  const portalTitle = (() => {
    switch (portalType) {
      case 'admin':
        return t('auth.shell.adminTitle');
      case 'business':
        return tenantBrandName;
      case 'consumer':
        return tenantTheme?.consumerPortalLabel ?? tenantBrandName;
      default:
        return tenantBrandName;
    }
  })();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        bgcolor: 'background.default',
        color: 'text.primary',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Theme toggle — floats over right pane top-right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 2 }}>
        <GlassTooltip title={mode === 'dark' ? t('auth.shell.switchToLight') : t('auth.shell.switchToDark')}>
          <GlassIconButton onClick={toggleMode} color="inherit" aria-label={t('auth.shell.toggleTheme')} size="medium">
            {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </GlassIconButton>
        </GlassTooltip>
      </Box>

      {/* LEFT: marketing pane (hidden on mobile) */}
      <Box
        component="aside"
        aria-hidden
        sx={(theme: Theme) => ({
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: { md: '52px 56px' },
          borderRight: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          overflow: 'hidden',
          background: `
            radial-gradient(ellipse 80% 60% at 25% 30%, ${alpha(theme.palette.success.main, 0.18)}, transparent 60%),
            radial-gradient(ellipse 60% 50% at 80% 80%, ${alpha(theme.palette.success.main, 0.08)}, transparent 60%),
            linear-gradient(180deg, ${theme.palette.background.default}, ${alpha(theme.palette.background.default, 1)})
          `,
        })}
      >
        {/* Brand badge — tenant brand (logo + name). "Powered by {platform}"
            attribution only shows on white-label tenants per §11d. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
          <BrandBlock label={tenantBrandName} logoUrl={tenantTheme?.logo} />
          {isWhiteLabel && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                pl: 4.5,
              }}
            >
              {t('auth.shell.poweredBy', { platformName })}
            </Typography>
          )}
        </Box>

        {/* Big tagline + Astor card mockup */}
        <Box>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              mb: 2,
              maxWidth: 480,
              color: 'text.primary',
            }}
          >
            {t('auth.shell.taglineLine1')}
            <br />
            {t('auth.shell.taglineLine2')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.6,
              mb: 4,
              maxWidth: 440,
            }}
          >
            {t('auth.shell.taglineSubtitle')}
          </Typography>

          {/* Astor recommendation card mockup */}
          <Box
            sx={(theme: Theme) => ({
              bgcolor: alpha(theme.palette.success.main, 0.06),
              border: '1px solid',
              borderColor: alpha(theme.palette.success.main, 0.25),
              borderRadius: 2,
              p: 2,
              maxWidth: 480,
              boxShadow: `0 0 0 1px ${alpha(theme.palette.success.main, 0.06)} inset`,
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={(theme: Theme) => ({
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                })}
              >
                <AutoAwesomeIcon sx={{ fontSize: iconSizes.xs, color: 'success.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
                  <GlassChip
                    label={t('auth.shell.astorCard.recommendation')}
                    size="small"
                    color="success"
                    sx={{ height: 22 }}
                  />
                  <GlassChip
                    label={t('auth.shell.astorCard.confidence')}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 22,
                      fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, color: 'text.primary' }}>
                  {t('auth.shell.astorCard.headline')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.55, mb: 1 }}
                >
                  {t('auth.shell.astorCard.detail')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.625, flexWrap: 'wrap' }}>
                  {['credit.pull', 'comps.auto_pull', 'policy.evaluate'].map((tool) => (
                    <Typography
                      key={tool}
                      variant="overline"
                      component="span"
                      sx={(theme: Theme) => ({
                        fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace',
                        color: 'success.main',
                        px: 0.75,
                        py: 0.25,
                        bgcolor: alpha(theme.palette.success.main, 0.15),
                        borderRadius: 0.5,
                        textTransform: 'none',
                        lineHeight: 1.4,
                      })}
                    >
                      {`↳ ${tool}`}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Trust line */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.25,
            color: 'text.disabled',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <ShieldIcon sx={{ fontSize: iconSizes['2xs'], color: 'success.main' }} />
            <Typography variant="caption">{t('auth.shell.trust.compliance')}</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'divider' }}>
            &middot;
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <CheckIcon sx={{ fontSize: iconSizes['2xs'], color: 'success.main' }} />
            <Typography variant="caption">{t('auth.shell.trust.funded')}</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'divider' }}>
            &middot;
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <GroupIcon sx={{ fontSize: iconSizes['2xs'], color: 'success.main' }} />
            <Typography variant="caption">{t('auth.shell.trust.tenants')}</Typography>
          </Box>
        </Box>
      </Box>

      {/* RIGHT: form pane (fixed 460px on desktop, full width on mobile) */}
      <Box
        component="section"
        sx={{
          width: { xs: '100%', md: 460 },
          flexShrink: 0,
          minHeight: '100vh',
          maxHeight: '100vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          borderLeft: { md: '1px solid' },
          borderColor: { md: 'divider' },
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 3, sm: 5 },
          py: { xs: 4, md: 6 },
        }}
      >
        {/* Tenant logo / portal title (above the form Outlet) */}
        <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
          {tenantTheme?.logo ? (
            <Box component="img" src={tenantTheme.logo} alt={tenantBrandName} sx={{ height: 40 }} />
          ) : (
            <Box
              component="img"
              src={mode === 'light' ? '/fiops-mark-light.svg' : '/fiops-mark-dark.svg'}
              alt={tenantBrandName}
              sx={{ height: 36, width: 36, borderRadius: 1.5 }}
            />
          )}
          {portalType && portalTitle && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              {portalTitle}
            </Typography>
          )}
        </Box>

        {/* Active auth page */}
        <Box sx={{ flex: 1 }}>
          <Outlet />
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', textAlign: 'center' }}>
            &copy; {new Date().getFullYear()} {t('auth.allRightsReserved', { company: tenantBrandName })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
